import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addDeal } from '../store/slices/pricingSlice';
import { addToast } from '../store/slices/toastsSlice';
import { v4 as uuidv4 } from 'uuid';

interface CreateDealModalProps {
  onClose: () => void;
}

const CreateDealModal: React.FC<CreateDealModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setQuantity(value);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPrice(value);
    }
  };

  const handleSave = () => {
    const parsedQuantity = parseInt(quantity);
    const parsedPrice = parseFloat(price);

    if (!quantity || !price || !startDate || !endDate) {
      dispatch(addToast('Please fill in all fields', 'error'));
      return;
    }

    if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
      dispatch(addToast('Please enter a valid quantity', 'error'));
      return;
    }

    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      dispatch(addToast('Please enter a valid price', 'error'));
      return;
    }

    // Validate dates
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      dispatch(addToast('End date must be after start date', 'error'));
      return;
    }

    const newDeal = {
      id: uuidv4(),
      quantity: parsedQuantity,
      price: parsedPrice,
      startDate,
      endDate
    };

    dispatch(addDeal(newDeal));
    dispatch(addToast('Deal created successfully!', 'success'));
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Create New Deal</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.formGroup}>
            <label htmlFor="quantity" style={styles.label}>
              Exact Quantity:
            </label>
            <input
              id="quantity"
              type="text"
              value={quantity}
              onChange={handleQuantityChange}
              onKeyDown={handleKeyDown}
              style={styles.input}
              placeholder="e.g., 100"
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="price" style={styles.label}>
              Price for that Quantity:
            </label>
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <span style={styles.dollarSign}>$</span>
              <input
                id="price"
                type="text"
                value={price}
                onChange={handlePriceChange}
                onKeyDown={handleKeyDown}
                style={{ ...styles.input, paddingLeft: '28px' }}
                placeholder="0.00"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="startDate" style={styles.label}>
              Start Date:
            </label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="endDate" style={styles.label}>
              End Date:
            </label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.saveButton} onClick={handleSave}>
            Save Deal
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '30px 20px'
  },
  formGroup: {
    marginBottom: '25px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  dollarSign: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666',
    fontSize: '16px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default CreateDealModal;
